import { Injectable } from '@angular/core';
import { OfficerDetailFacade } from '@intrensic/shared/officer-details';
import { BaseApiService } from '@shared/data-access';
import { AgencyDetailsFacade } from '@shared/feature/agency-details';
import { UploadImageService } from '@shared/feature/upload-file';
import { AppConfig } from '@shared/utils/app-config';
import { UserRole } from '@shared/utils/base-auth';
import { Observable, Subject, first, map, switchMap, tap } from 'rxjs';
import { TranslationFromJsonService } from '@shared/feature/translate';

@Injectable({
  providedIn: 'root',
})
export class IntrensicAgencyUploadImageService extends UploadImageService {
  private uploadDoneSubject = new Subject<boolean>();

  constructor(
    api: BaseApiService,
    private officerDetailsFacade: OfficerDetailFacade,
    private agencyFacade: AgencyDetailsFacade,
    private appConfig: AppConfig,
    private translationFromJsonService: TranslationFromJsonService
  ) {
    super(api);
  }

  override isLoading(): Observable<boolean> {
    return this.agencyFacade.isImageLoading();
  }

  override uploadDone(): Observable<boolean> {
    return this.uploadDoneSubject.asObservable();
  }

  override getImage(): Observable<string> {
    return this.agencyFacade.getAgencyPhoto().pipe(map((x) => x ?? this.appConfig.statics.blueLogo));
  }

  override uploadImage(image: File): void {
    this.officerDetailsFacade
      .getLoggedInOfficer()
      .pipe(
        first(),
        switchMap((officer) => {
          const role = officer.role;
          if (role === UserRole.HRAdmin || role === UserRole.SuperAdmin) {
            this.agencyFacade.updateAgencyPhoto(image);
          }

          return '';
        })
      )
      .subscribe();
  }

  override uploadImageApi(image: File): Observable<string> {
    const form = new FormData();
    form.append('file', image);

    return this.api.postFormData('api/customers/picture', form).pipe(
      map((url) => `${url}?t=${Date.now()}`),
      tap(() => this.uploadDoneSubject.next(true))
    );
  }

  override getTitle(): string {
    return this.translationFromJsonService.instant('upload-image-customer-title');
  }
}
